'use client'

import { v4 as uuidv4 } from 'uuid'

import { getSession, getSessionBasket } from '@/utils'

// sessionStorage can throw (SSR, disabled/private mode, corrupt JSON) — never let logging fail because of it.
const readSessionContext = (): { basketId?: string; sessionId?: string } => {
  try {
    return { basketId: getSessionBasket()?.basketId, sessionId: getSession()?.sessionId }
  } catch {
    return {}
  }
}

const postLog = (level: 'error' | 'warning' | 'info', message: string, payload?: Record<string, unknown>) => {
  fetch(`/api/booking-experience/log/${level}`, {
    body: JSON.stringify({
      ...readSessionContext(),
      ...payload,
      correlationId: payload?.correlationId ?? uuidv4(),
      message,
      // Defaults to the message; callers may pass a stable messageTemplate (e.g. transport errors) for easier Kibana filtering.
      messageTemplate: (payload?.messageTemplate as string | undefined) ?? message,
    }),
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    method: 'POST',
  }).catch(console.error)
}

export const logFrontendError = (message: string, payload?: Record<string, unknown>) => postLog('error', message, payload)

export const logFrontendWarning = (message: string, payload?: Record<string, unknown>) => postLog('warning', message, payload)

export const logFrontendInfo = (message: string, payload?: Record<string, unknown>) => postLog('info', message, payload)

// Browser fetch() rejected with a TypeError — the request never completed. Benign navigation/connection noise.
// - 'Load failed'     → WebKit/Safari (iOS & macOS)
// - 'Failed to fetch' → Chromium (Chrome/Edge) and Firefox equivalent
export const isTransportError = (message?: string): boolean => /load failed|failed to fetch/i.test(message ?? '')

// Next.js client-side Server Action error: the RPC round-trip completed but the response wasn't a valid action reply
// (4xx/5xx, HTML/redirect page, WAF block, or a stale action id after a deploy). Worth monitoring for spikes.
export const isServerActionError = (message?: string): boolean => /unexpected response was received/i.test(message ?? '')

// Stable template so every transport failure groups together in Kibana, regardless of the originating message.
export const TRANSPORT_ERROR_TEMPLATE = 'Frontend transport error'

// Stable template so every Server Action failure groups together in Kibana, regardless of the originating message.
export const SERVER_ACTION_ERROR_TEMPLATE = 'Frontend server action error'

// Severity policy for a caught error: transport noise → info, everything else → error — each under a dedicated template.
// Server Action failures are kept at error for now to investigate their frequency/cause in production (revert to warning once understood).
export const pickFrontendLog = (message?: string) => {
  if (isTransportError(message)) {
    return (msg: string, payload?: Record<string, unknown>) =>
      logFrontendInfo(msg, { ...payload, messageTemplate: TRANSPORT_ERROR_TEMPLATE })
  }

  if (isServerActionError(message)) {
    return (msg: string, payload?: Record<string, unknown>) =>
      logFrontendError(msg, { ...payload, messageTemplate: SERVER_ACTION_ERROR_TEMPLATE })
  }

  return logFrontendError
}
